import { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Typography, makeStyles } from '@material-ui/core';
import TextBoxWithTooltip from '../textBoxWithTooltip';
import DotsMenu from '../dotsMenu';
import ActionButton from '../actionButton';
import RichTextPreview from '../richTextPreview';
import UserAvatar from '../userAvatar';
import ConditionalTooltip from '../conditionalTooltip';
import { ACTION_BUTTON_TYPES } from '../actionButton/config';
import { getPersonFullName } from '../../../utility/uiUtils';
import { formatDate } from '../../../utility/dateUtils';
import {
  isAnnouncementPublishedCheck,
  prepareAnnouncementAttachmentInfo,
  prepareAnnouncementMenuItems,
} from '../../../utility/announcements';
import { ReactComponent as FileIcon } from '../../../assets/icons/insert_drive_file.svg';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    backgroundColor: primary.white,
    borderRadius: 16,
    border: `1px solid ${primary.bluish7}`,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 180,
    padding: spacing(6, 6, 2, 6),
    minWidth: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: spacing(8),
    minWidth: 0,
  },
  titleWrapper: {
    flex: 1,
    minWidth: 0,
  },
  meta: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    marginLeft: spacing(4),
  },
  date: {
    color: primary.bluish4,
    marginRight: spacing(2),
    whiteSpace: 'nowrap',
  },
  description: {
    color: primary.bluish3,
    marginBottom: spacing(3),
  },
  file: props => ({
    alignItems: 'center',
    backgroundColor: props.backgroundColor,
    border: `1px solid ${primary.bluish7}`,
    borderRadius: 8,
    display: 'flex',
    padding: spacing(3, 4),
    marginBottom: spacing(3),
  }),
  fileIcon: props => ({
    flexShrink: 0,
    height: 20,
    marginRight: spacing(3),
    width: 20,
    '& path': {
      fill: props.color,
    },
  }),
  fileInfo: {
    flex: 1,
    minWidth: 0,
  },
  fileName: {
    lineHeight: '18px',
    marginBottom: spacing(0.5),
  },
  fileMeta: {
    color: primary.bluish4,
    lineHeight: '16px',
  },
  footer: {
    alignItems: 'center',
    borderTop: `1px solid ${primary.bluish7}`,
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 'auto',
    paddingTop: spacing(2),
  },
  footerItem: {
    alignItems: 'center',
    display: 'flex',
    gap: spacing(3),
    minWidth: 0,
  },
  footerLabel: {
    color: primary.bluish4,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  avatarTooltip: {
    display: 'flex',
  },
}));

const AnnouncementCard = ({
  translations,
  announcement,
  category,
  canManage,
  onPublish,
  onUnpublish,
  onEdit,
  onDelete,
}) => {
  const { color, backgroundColor } = category;
  const classes = useStyles({ color, backgroundColor });
  const {
    title,
    body,
    attachment,
    author,
    published_by,
    published_at,
    publishedAt,
  } = announcement;
  const isPublished = isAnnouncementPublishedCheck(announcement);
  const publishedDate = published_at ?? publishedAt;
  const attachmentInfo = useMemo(
    () => prepareAnnouncementAttachmentInfo(attachment),
    [attachment]
  );

  const onDownloadAttachment = useCallback(() => {
    if (!attachmentInfo?.url) {
      return;
    }
    window.open(attachmentInfo.url, '_blank', 'noopener noreferrer');
  }, [attachmentInfo]);

  const renderUser = user => (
    <ConditionalTooltip
      className={classes.avatarTooltip}
      message={getPersonFullName(user)}
      addTooltip
    >
      <UserAvatar user={user} small />
    </ConditionalTooltip>
  );

  const renderPublishedBy = () => {
    if (published_by) {
      return renderUser(published_by);
    }

    return <Typography variant="subtitle2">-</Typography>;
  };

  const menuItems = useMemo(
    () =>
      canManage
        ? prepareAnnouncementMenuItems(
            translations.menuItems,
            announcement,
            onEdit,
            onDelete,
            onPublish,
            onUnpublish
          )
        : [],
    [
      announcement,
      canManage,
      onDelete,
      onEdit,
      onPublish,
      onUnpublish,
      translations.menuItems,
    ]
  );

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.titleWrapper}>
          <TextBoxWithTooltip text={title} variant="h4" />
        </div>
        <div className={classes.meta}>
          {isPublished && publishedDate && (
            <Typography className={classes.date} variant="body2">
              {formatDate(publishedDate)}
            </Typography>
          )}
          {canManage && <DotsMenu menuItems={menuItems} />}
        </div>
      </div>
      <RichTextPreview className={classes.description} text={body} />
      {attachmentInfo && (
        <div className={classes.file}>
          <FileIcon className={classes.fileIcon} />
          <div className={classes.fileInfo}>
            <TextBoxWithTooltip
              className={classes.fileName}
              text={attachmentInfo.name}
              variant="subtitle1"
            />
            {attachmentInfo.typeLabel && (
              <Typography className={classes.fileMeta} variant="body2">
                {attachmentInfo.typeLabel}
              </Typography>
            )}
          </div>
          <ActionButton
            type={ACTION_BUTTON_TYPES.DOWNLOAD}
            tooltipText={translations.downloadFile}
            onClickHandler={onDownloadAttachment}
          />
        </div>
      )}
      <div className={classes.footer}>
        <div className={classes.footerItem}>
          <Typography className={classes.footerLabel} variant="body2">
            {translations.createdBy}
          </Typography>
          {author ? (
            renderUser(author)
          ) : (
            <Typography variant="subtitle2">-</Typography>
          )}
        </div>
        <div className={classes.footerItem}>
          {!isPublished ? (
            <Typography className={classes.footerLabel} variant="body2">
              {translations.unpublished}
            </Typography>
          ) : (
            <>
              <Typography className={classes.footerLabel} variant="body2">
                {translations.publishedBy}
              </Typography>
              {renderPublishedBy()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

AnnouncementCard.propTypes = {
  translations: PropTypes.object.isRequired,
  announcement: PropTypes.object.isRequired,
  category: PropTypes.shape({
    color: PropTypes.string.isRequired,
    backgroundColor: PropTypes.string.isRequired,
  }).isRequired,
  canManage: PropTypes.bool.isRequired,
  onPublish: PropTypes.func.isRequired,
  onUnpublish: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default AnnouncementCard;
